import { useAuth } from '../contexts/AuthContext';
import { useMenuSettings } from '../contexts/MenuSettingsContext';
import { useUserPermissions } from '../contexts/UserPermissionsContext';

/** The two levels of the compliance (supplier onboarding) gate. */
export interface ComplianceAccess {
  /** The tenant has compliance: Navigation Menu Management › Supplier Onboarding is on. */
  enabled: boolean;
  /** This user may use it: the tenant has it and, for a customer user, the Compliance permission is ticked. */
  canUse: boolean;
}

/**
 * CR-8252 (decision 2): whether compliance exists for this tenant and whether
 * this user may use it. The same two levels the server enforces on every
 * onboarding endpoint (`backend/onboarding/access.py`), so a screen never
 * offers what the API would refuse.
 *
 * Supplier users are not gated by the per-user permission: a supplier has
 * compliance because it is being onboarded.
 *
 * @returns `{ enabled, canUse }`
 * @example
 *   const { canUse } = useComplianceAccess();
 *   if (!canUse) return null;
 */
export function useComplianceAccess(): ComplianceAccess {
  const { user } = useAuth();
  const { isMenuItemVisible } = useMenuSettings();
  const { hasModuleAccess } = useUserPermissions();
  const enabled = isMenuItemVisible('onboarding');
  const isSupplier = user?.company_type === 'supplier';
  return { enabled, canUse: enabled && (isSupplier || hasModuleAccess('compliance')) };
}
